import { useState, type ReactNode } from 'react';
import { Lock } from 'lucide-react';
import { useMutation } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import { IssueReportJourneySteps } from '@/components/common/IssueReportJourneySteps';
import { SectionCard } from '@/components/common/SectionCard';
import { Button } from '@/components/ui/Button';
import { EmptyState } from '@/components/ui/EmptyState';
import { ErrorState } from '@/components/ui/ErrorState';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { useEntity } from '@/hooks/useEntity';
import { useDataRoomAccess } from '@/hooks/useDataRoomAccess';
import { peekPurchaseIntent } from '@/lib/auth/intent-storage';
import { BILLING_REVIEW_PLAN_PARAM, ROUTES } from '@/lib/constants/routes';
import type { CheckoutQuoteParams, Plan } from '@/lib/types/billing';
import { getApiErrorMessage } from '@/lib/utils/error.utils';
import { formatCents } from '@/lib/utils/format.utils';
import { createCheckoutSession, getCheckoutQuote } from './api/billing.api';
import { BillingPageLayout } from './components/BillingPageLayout';
import { OrderSummary } from './components/OrderSummary';
import { formatAccessTerm } from './access-term';
import { INTERVAL_SUFFIX } from './components/PlanSummaryCard';
import { PromoCodeField } from './components/PromoCodeField';
import { BILLING_STRINGS } from './constants/billing-strings';
import { LIVE_SUBSCRIPTION_STATUSES } from './constants/subscription-status';
import { useCheckoutCatalogue } from './hooks/useCheckoutCatalogue';
import { useCheckoutQuote } from './hooks/useCheckoutQuote';
import { useIssueReportOffer } from '@/hooks/useIssueReportOffer';
import { ISSUE_REPORT_OFFER_STATE } from '@/lib/constants/issue-report';

const COPY = BILLING_STRINGS.REVIEW;
const PURCHASE = BILLING_STRINGS.PURCHASE;

/**
 * The review step between choosing a plan and Stripe (FR-39; Sprint 7 QA).
 *
 * The standard last step before payment: the chosen plan, how many extra
 * seats, a promo code, and the total that results. Every figure is the
 * backend's quote, priced with the same checks a checkout session makes, so
 * the total shown is the one Stripe charges and a code that applies here
 * cannot be refused at checkout.
 *
 * The plan is in the URL (`?plan=`), so a refresh or a shared link keeps it.
 */
export function CheckoutReviewPage() {
  const planId = useSearchParams()[0].get(BILLING_REVIEW_PLAN_PARAM);
  const catalogue = useCheckoutCatalogue();
  const access = useDataRoomAccess(useEntity()?.entityId ?? null);

  if (catalogue.isLoading || access.isLoading) {
    return (
      <ReviewLayout>
        <LoadingSpinner />
      </ReviewLayout>
    );
  }
  if (catalogue.isError || !catalogue.data) {
    return (
      <ReviewLayout>
        <ErrorState title={getApiErrorMessage(catalogue.error, COPY.TITLE)} />
      </ReviewLayout>
    );
  }

  const data = catalogue.data;
  const plan = data.plans.find((candidate) => candidate.id === planId);
  const live =
    data.currentSubscription && LIVE_SUBSCRIPTION_STATUSES.includes(data.currentSubscription.status);
  // SR NEXT Premium is access with no subscription behind it (A-109: nothing
  // can be bought while any access is open), so the access view decides too.
  const current = access.data?.current ?? null;
  const heldName = live
    ? (data.plans.find((candidate) => candidate.id === data.currentSubscription?.planId)?.name ??
      PURCHASE.PREMIUM_FALLBACK)
    : current
      ? BILLING_STRINGS.OVERVIEW.SERVICE_LABELS[current.service]
      : null;

  return (
    <ReviewLayout>
      {heldName ? (
        // The backend refuses a second purchase, so say so before the member
        // fills anything in rather than at the final click.
        <SectionCard>
          <div className="flex flex-wrap items-center gap-4" role="status">
            <p className="text-text-secondary text-sm">
              {PURCHASE.ALREADY_SUBSCRIBED.replace('{plan}', heldName)}
            </p>
            <Button asChild variant="outline">
              <Link to={ROUTES.BILLING}>{PURCHASE.GO_TO_BILLING}</Link>
            </Button>
          </div>
        </SectionCard>
      ) : plan ? (
        <ReviewForm plan={plan} />
      ) : (
        // A missing, withdrawn or hand-edited plan id.
        <SectionCard>
          <EmptyState title={COPY.PLAN_UNAVAILABLE_TITLE} description={COPY.PLAN_UNAVAILABLE_BODY} />
        </SectionCard>
      )}
    </ReviewLayout>
  );
}

/**
 * The review step's frame: "Review your purchase" as the heading where the
 * "Change plan" link used to be, and no page header above it (UX round 3,
 * StartupAdminPatty Y6). There is only the Issue Report on sale, so going
 * back to change plan offered a choice that does not exist.
 */
function ReviewLayout({ children }: { children: ReactNode }) {
  // On the Issue Report journey, for the Issue Report's own plan, the progress
  // bar names the step, so the heading is shown to screen readers only.
  const planId = useSearchParams()[0].get(BILLING_REVIEW_PLAN_PARAM);
  const { data: offer } = useIssueReportOffer();
  const inJourney =
    peekPurchaseIntent() !== null && offer?.state === ISSUE_REPORT_OFFER_STATE.AVAILABLE && offer.planId === planId;
  return (
    <BillingPageLayout>
      <div className="mx-auto flex w-full max-w-2xl flex-col gap-6">
        {inJourney && <IssueReportJourneySteps stage="payment" className="mb-2" />}
        <h1 className={`text-text-primary text-xl font-semibold ${inJourney ? 'sr-only' : ''}`.trim()}>
          {COPY.TITLE}
        </h1>
        {children}
      </div>
    </BillingPageLayout>
  );
}

/**
 * Promo code, the priced order and the way to Stripe, in one card with the
 * promo code above the "Due today" total (UX round 3, StartupAdminPatty Y7:
 * the two side-by-side cards read as two separate steps). No seat count: a
 * data-room service carries a fixed number of seats and none can be bought
 * (FR-138).
 *
 * @param plan - the chosen plan, known to be on sale
 */
function ReviewForm({ plan }: { plan: Plan }) {
  const entityId = useEntity()?.entityId;
  const [appliedCode, setAppliedCode] = useState<string | null>(null);

  const params: CheckoutQuoteParams = { planId: plan.id, promoCode: appliedCode ?? undefined };
  const quote = useCheckoutQuote(params);

  // A code is applied only once the backend has priced the order with it, so
  // a refused code never reaches the summary or the checkout session.
  const applyPromo = useMutation({
    mutationFn: (code: string) =>
      getCheckoutQuote(entityId as string, { planId: plan.id, promoCode: code }),
    onSuccess: (priced) => setAppliedCode(priced.discount?.code ?? null),
  });

  const checkout = useMutation({
    mutationFn: () =>
      createCheckoutSession(entityId as string, {
        planId: plan.id,
        promoCode: appliedCode ?? undefined,
      }),
    // The browser leaves for Stripe; nothing after this renders, so there is
    // no success toast. A refusal stays inline beside the button.
    // The intent stays until the confirmed return, so that last screen still
    // shows the journey's progress bar (FR-224).
    onSuccess: ({ checkoutUrl }) => window.location.assign(checkoutUrl),
  });

  const canContinue =
    Boolean(quote.data) &&
    !quote.isPlaceholderData &&
    !quote.isError &&
    !applyPromo.isPending &&
    !checkout.isPending;

  return (
    <SectionCard>
      <div className="flex flex-col gap-6">
        <div>
          <p className="text-text-primary text-lg font-semibold">{plan.name}</p>
          <p className="text-text-secondary text-sm">
            {[
              `${formatCents(plan.priceCents) ?? ''}${INTERVAL_SUFFIX[plan.billingInterval]}`,
              // Card 9 step 2: the Issue Report's 7-day term is stated here.
              formatAccessTerm(plan),
              plan.seatLimit === 1
                ? COPY.INCLUDED_SEAT_ONE
                : COPY.INCLUDED_SEATS.replace('{count}', String(plan.seatLimit)),
            ]
              .filter(Boolean)
              .join(' - ')}
          </p>
        </div>

        {/* Full width, so Apply sits at the card's right edge (approved journey screens). */}
        <div>
          <PromoCodeField
            applied={appliedCode}
            error={
              applyPromo.isError ? getApiErrorMessage(applyPromo.error, COPY.PROMO_FAILED) : undefined
            }
            isApplying={applyPromo.isPending}
            onApply={(code) => applyPromo.mutate(code)}
            onRemove={() => setAppliedCode(null)}
            onEdit={() => applyPromo.reset()}
          />
        </div>

        {quote.data ? <OrderSummary quote={quote.data} /> : <LoadingSpinner size="sm" />}
        {quote.isError && (
          <p role="alert" className="text-danger text-sm">
            {getApiErrorMessage(quote.error, COPY.QUOTE_FAILED)}
          </p>
        )}
        <Button className="w-full" onClick={() => checkout.mutate()} disabled={!canContinue}>
          {PURCHASE.SUBMIT}
        </Button>
        <p className="text-text-muted flex items-start gap-1.5 text-xs">
          <Lock aria-hidden="true" className="mt-px h-3.5 w-3.5 shrink-0" />
          {PURCHASE.REDIRECT_NOTE}
        </p>
        {checkout.isError && (
          <p role="alert" className="text-danger text-sm">
            {getApiErrorMessage(checkout.error, PURCHASE.SUBMIT)}
          </p>
        )}
      </div>
    </SectionCard>
  );
}
